<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NDrawer,
  NDrawerContent,
  NForm,
  NFormItem,
  NInput,
  NSelect,
  NButton,
  useMessage,
} from 'naive-ui';
import { Mail, UserPlus } from 'lucide-vue-next';
import { membersApi } from '@/services/api';
import type { UserRole } from '@/types/models';

interface Props {
  show: boolean;
  teamOptions?: { label: string; value: string }[];
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  teamOptions: () => [],
});

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'invited'): void;
}>();

const { t } = useI18n();
const naiveMsg = useMessage();
const isSubmitting = ref(false);

const form = ref<{
  email: string;
  role: UserRole;
  teamId: string | null;
}>({
  email: '',
  role: 'COACH',
  teamId: null,
});

const roleOptions = computed<{ label: string; value: UserRole }[]>(() => [
  { label: `Admin (${t('memberInvite.adminDesc').slice(0, 30)}...)`, value: 'ADMIN' },
  { label: `Coach (${t('memberInvite.coachDesc').slice(0, 30)}...)`, value: 'COACH' },
  { label: `Player (${t('memberInvite.playerDesc').slice(0, 30)}...)`, value: 'PLAYER' },
  { label: `Guardian (${t('memberInvite.guardianDesc').slice(0, 30)}...)`, value: 'GUARDIAN' },
]);

const handleSendInvitation = async () => {
  if (!form.value.email || !form.value.email.includes('@')) {
    naiveMsg.error(t('memberInvite.invalidEmail'));
    return;
  }

  isSubmitting.value = true;
  try {
    await membersApi.inviteMembers([
      {
        email: form.value.email.trim(),
        role: form.value.role,
        teamId: form.value.teamId || undefined,
      },
    ]);

    naiveMsg.success(t('memberInvite.successMsg', { email: form.value.email }));
    emit('invited');
    emit('update:show', false);

    form.value = {
      email: '',
      role: 'COACH',
      teamId: null,
    };
  } catch (err: any) {
    naiveMsg.error(err.message || 'Failed to dispatch invitation');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <NDrawer
    :show="show"
    @update:show="(val) => emit('update:show', val)"
    :width="420"
    placement="right"
  >
    <NDrawerContent :title="t('memberInvite.drawerTitle')" closable>
      <div class="space-y-6 pt-2">
        <div class="p-4 rounded-2xl bg-sky-500/10 border border-sky-500/20 text-xs text-sky-200 flex items-start gap-3">
          <Mail class="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
          <p class="leading-relaxed">
            {{ t('memberInvite.infoTip') }}
          </p>
        </div>

        <NForm @submit.prevent="handleSendInvitation" class="space-y-4">
          <NFormItem :label="t('memberInvite.emailLabel')" required>
            <NInput
              v-model:value="form.email"
              type="text"
              :placeholder="t('memberInvite.emailPlaceholder')"
              size="medium"
            />
          </NFormItem>

          <NFormItem :label="t('memberInvite.roleLabel')" required>
            <NSelect
              v-model:value="form.role"
              :options="roleOptions"
              size="medium"
            />
          </NFormItem>

          <NFormItem :label="t('memberInvite.teamLabel')">
            <NSelect
              v-model:value="form.teamId"
              :options="teamOptions"
              :placeholder="t('memberInvite.teamPlaceholder')"
              clearable
              size="medium"
            />
          </NFormItem>

          <div class="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-1 text-[11px] text-slate-400">
            <span class="font-bold text-slate-300">{{ t('memberInvite.rolePermissions') }}</span>
            <p v-if="form.role === 'ADMIN'">
              &bull; <strong>ADMIN</strong>: {{ t('memberInvite.adminDesc') }}
            </p>
            <p v-else-if="form.role === 'COACH'">
              &bull; <strong>COACH</strong>: {{ t('memberInvite.coachDesc') }}
            </p>
            <p v-else-if="form.role === 'PLAYER'">
              &bull; <strong>PLAYER</strong>: {{ t('memberInvite.playerDesc') }}
            </p>
            <p v-else>
              &bull; <strong>GUARDIAN</strong>: {{ t('memberInvite.guardianDesc') }}
            </p>
          </div>

          <div class="pt-4 flex items-center justify-end gap-3 border-t border-slate-800">
            <NButton quaternary @click="emit('update:show', false)">
              {{ t('memberInvite.cancelBtn') }}
            </NButton>
            <NButton
              type="primary"
              attr-type="submit"
              :loading="isSubmitting"
              class="font-bold text-xs shadow-md"
            >
              <template #icon><UserPlus class="w-4 h-4" /></template>
              {{ t('memberInvite.sendBtn') }}
            </NButton>
          </div>
        </NForm>
      </div>
    </NDrawerContent>
  </NDrawer>
</template>
